import { Component, Input, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import {
  TransportStockBalanceItem,
  TransportStockMovementSummary,
  WholesalerDefinitionPublic,
  WholesalerStoreConfig,
} from 'src/app/models/invoice-import.model';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import { WholesalerConfigDialogComponent } from '../wholesaler-config-dialog/wholesaler-config-dialog.component';

@Component({
  selector: 'app-invoice-import-settings',
  templateUrl: './invoice-import-settings.component.html',
  styleUrls: ['./invoice-import-settings.component.scss'],
})
export class InvoiceImportSettingsComponent implements OnInit {
  @Input() storeId!: string;

  inboundAddress = '';
  configs: WholesalerStoreConfig[] = [];
  definitions: WholesalerDefinitionPublic[] = [];
  transportBalances: TransportStockBalanceItem[] = [];
  transportMovements: TransportStockMovementSummary[] = [];
  transportLoadError = '';
  loadError = '';

  constructor(
    private invoiceImportService: InvoiceImportService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadTransportStock();
  }

  loadTransportStock(): void {
    this.transportLoadError = '';
    this.invoiceImportService.getTransportStock(this.storeId).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.transportBalances = data.items || [];
        this.transportMovements = data.movements || [];
      },
      error: (err: any) => {
        this.transportLoadError = err.error?.response?.message || err.message;
      },
    });
  }

  load(): void {
    this.loadError = '';
    this.spinner.show();
    this.invoiceImportService.getStoreWholesalers(this.storeId).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.inboundAddress = data.inboundAddress || '';
        this.configs = data.configs || [];
        this.definitions = data.definitions || [];
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.loadError = err.error?.response?.message || err.message;
      },
    });
  }

  definitionFor(key: string): WholesalerDefinitionPublic | undefined {
    return this.definitions.find((d) => d.supplierKey === key);
  }

  configFor(key: string): WholesalerStoreConfig | undefined {
    return this.configs.find((c) => c.supplierKey === key);
  }

  openEditor(def: WholesalerDefinitionPublic): void {
    const existing = this.configFor(def.supplierKey);
    this.dialogService
      .open(WholesalerConfigDialogComponent, {
        context: {
          storeId: this.storeId,
          definition: def,
          config: existing,
        },
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((saved) => {
        if (saved) {
          this.load();
        }
      });
  }

  deactivate(config: WholesalerStoreConfig): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.SETTINGS.DEACTIVATE_TITLE'),
      icon: 'warning',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.invoiceImportService.deactivateStoreWholesaler(this.storeId, config.supplierKey).subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('INVOICE_IMPORTS.SETTINGS.DEACTIVATED'));
          this.load();
        },
        error: (err: any) => {
          Swal.fire('Error', err.error?.response?.message || err.message, 'error');
        },
      });
    });
  }

  copyInbound(): void {
    if (!this.inboundAddress) {
      return;
    }
    navigator.clipboard?.writeText(this.inboundAddress);
    this.toastr.success(this.translate.instant('INVOICE_IMPORTS.OVERVIEW.COPIED'));
  }
}
